import { Grid, Typography } from "@mui/material";
import { Fragment, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import useTranslation from "shared/helpers/hooks/useTranslation";
import { requiredRules } from "shared/helpers/validators";
import ButtonLoader from "shared/ui/ButtonLoader";
import TextInput from "shared/ui/TextInput";
import { ERequestStatus } from "store/enums/index.enum";
import { AppDispatch } from "store/index";
import { ITriggerServices } from "store/interfaces/triggerServices";
import {
  selectButtonLoadingState,
  setButtonLoading,
} from "store/slicers/common";
import {
  AddTriggerService,
  UpdateTriggerService,
} from "store/slicers/triggerServices";

interface IFormData {
  name: string;
  typeId: string;
}

const AddEditTriggerService = ({
  onSuccess,
  editData,
}: {
  onSuccess: () => void;
  editData?: ITriggerServices;
}) => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const methods = useForm<IFormData>({
    defaultValues: {
      name: "",
    },
  });
  const isLoading = useSelector(selectButtonLoadingState);

  const onSubmit = async (data: IFormData) => {
    dispatch(setButtonLoading(true));

    if (editData) {
      const { meta, payload } = await dispatch(
        UpdateTriggerService({ id: editData?.id, ...data })
      );
      if (!payload?.success) {
        dispatch(setButtonLoading(false));
        toast.error(payload?.message || "");
        return;
      }
      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        dispatch(setButtonLoading(false));
        return;
      }
    } else {
      const { meta, payload } = await dispatch(AddTriggerService(data));
      if (!payload?.success) {
        dispatch(setButtonLoading(false));
        toast.error(payload?.message || "");
        return;
      }
      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        dispatch(setButtonLoading(false));
        return;
      }
    }

    const message = editData
      ? "Service Types Updated Successfully"
      : "Service Types Added Successfully";
    toast.success(message);
    dispatch(setButtonLoading(false));
    onSuccess();
  };

  const initialFetch = useCallback(async () => {
    if (editData) {
      methods.reset({
        name: editData.name ?? "",
        typeId: editData.typeId ?? "",
      });
    }
  }, [editData, methods]);

  useEffect(() => {
    initialFetch();
  }, [initialFetch]);

  return (
    <Fragment>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                label="name"
                name="name"
                rules={{
                  ...requiredRules,
                  maxLength: {
                    value: 300,
                    message: "Max 300 characters",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput label="type" name="typeId" rules={requiredRules} />
            </Grid>

            <Grid item xs={12}>
              <ButtonLoader
                fullWidth
                onClick={methods.handleSubmit(onSubmit)}
                isLoading={isLoading}
                type="submit"
              >
                <Typography>{t("save")}</Typography>
              </ButtonLoader>
            </Grid>
          </Grid>
        </FormProvider>
      </form>
    </Fragment>
  );
};

export default AddEditTriggerService;
